/**
 * CompletedRitualCard Component
 * Card variant for completed rituals with checkmark overlay, darkened image, and Reflect CTA.
 * Navigates to AI chat with ritual context when Reflect is clicked.
 */

import { Check } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { useCreateOnDemandReflect } from "@/features/chats/hooks/useAiChat"
import { formatRitualType } from "@/features/rituals/types"
import { formatDuration } from "@/features/rituals/utils/formatDuration"
import { useTranslation } from "@/language"
import { cn } from "@/lib"

interface CompletedRitualCardProps {
  id: string
  title: string
  type: string
  participationFee: string
  duration: number
  image?: string
  creatorName?: string
  completedAt?: string | null
  className?: string
}

export function CompletedRitualCard({
  id,
  title,
  type,
  participationFee,
  duration,
  image,
  className,
}: CompletedRitualCardProps) {
  const { t } = useTranslation("myRituality")
  const navigate = useNavigate()
  const createReflect = useCreateOnDemandReflect()

  const handleReflect = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (createReflect.isPending) return
    createReflect.mutate(id, {
      onSuccess: result => {
        navigate(`/chats/ai/${result.id}`)
      },
      onError: () => {
        toast.error(t("reflection.error"))
      },
    })
  }

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-tl-[40px] rounded-br-[40px] h-[260px]  md:h-[340px] cursor-pointer",
        className,
      )}
      onClick={() => navigate(`/rituals/${id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          navigate(`/rituals/${id}`)
        }
      }}
    >
      {/* Background image */}
      {image ? (
        <img
          src={image}
          alt={title}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-card" />
      )}

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[rgba(0,0,0,0.4)]" />

      {/* Content - centered */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-6 text-center">
        {/* Checkmark circle */}
        <div className="w-[50px] h-[50px] md:w-[70px] md:h-[70px] rounded-full border-gradient-glass flex items-center justify-center">
          <Check className="w-[30px] h-[30px] text-white" strokeWidth={2} />
        </div>

        {/* Title */}
        <h3 className="text-sm md:text-lg font-semibold text-white opacity-90 mt-5 md:mt-6 leading-snug max-w-[400px]">
          {title}
        </h3>

        {/* Tags */}
        <div className="flex items-center gap-2.5 mt-2">
          <span
            className={`text-[13px] text-white rounded px-1.5 py-0.5 ${
              participationFee === "Free"
                ? "bg-olive"
                : participationFee === "Donation-based"
                  ? "bg-brand-green"
                  : "bg-brand-terracotta"
            }`}
          >
            {participationFee}
          </span>
          <span className="text-[13px] text-white">{formatRitualType(type)}</span>
          <span className="text-[13px] text-white">{formatDuration(duration)}</span>
        </div>

        {/* Reflect button */}
        <Button
          variant="outline"
          className="mt-2.5 md:mt-6 h-[50px] w-[150px] md:w-[180px] rounded-full uppercase font-semibold text-sm"
          onClick={handleReflect}
          disabled={createReflect.isPending}
        >
          {createReflect.isPending ? t("latestRituals.opening") : t("latestRituals.reflect")}
        </Button>
      </div>
    </div>
  )
}
